import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { History, X } from "lucide-react";
import { Session } from "../../types";

interface HistoryPanelProps {
    isOpen: boolean;
    onClose: () => void;
    sessions: Session[];
}

export function HistoryPanel({ isOpen, onClose, sessions }: HistoryPanelProps) {
    return (
        <AnimatePresence>
            {isOpen && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/50 z-50"
                        onClick={onClose}
                    />
                    {/* Panel */}
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
                        <motion.div
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                            className="w-full max-w-lg max-h-[80vh] bg-white dark:bg-[#1a1a24] rounded-2xl shadow-2xl overflow-hidden pointer-events-auto flex flex-col"
                        >
                            {/* Header */}
                            <div className="px-5 py-4 border-b border-siemens-deep-blue/5 dark:border-[#3d3d4d] flex items-center justify-between bg-gradient-to-r from-siemens-feedback-orange to-orange-600">
                                <h2 className="text-lg font-bold text-white flex items-center gap-2">
                                    <History className="w-5 h-5" />
                                    Session History
                                </h2>
                                <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/20 transition-colors">
                                    <X className="w-5 h-5 text-white" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto p-5">
                                {sessions.length === 0 ? (
                                    <div className="text-center py-12">
                                        <History className="w-12 h-12 text-siemens-text-soft mx-auto mb-3 opacity-50" />
                                        <p className="text-sm font-medium text-siemens-deep-blue dark:text-white">No history yet</p>
                                        <p className="text-xs text-siemens-text-soft mt-1">Your prioritization sessions will appear here</p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {sessions.slice(0, 10).map((session, idx) => (
                                            <div key={idx} className="p-4 rounded-lg bg-siemens-deep-blue-8/50 dark:bg-[#13131a] border border-siemens-deep-blue/5 dark:border-[#3d3d4d]">
                                                <div className="flex items-center justify-between mb-2">
                                                    <span className="text-sm font-semibold text-siemens-deep-blue dark:text-white">
                                                        Session {sessions.length - idx}
                                                    </span>
                                                    <span className="text-xs text-siemens-text-soft">
                                                        {new Date(session.timestamp || session.createdAt || Date.now()).toLocaleDateString()}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-siemens-text-soft">
                                                    {/* Display epic count if changes not available */}
                                                    {session.epics?.length ? `${session.epics.length} epics prioritized` : 'Session data saved'}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-5 py-4 border-t border-siemens-deep-blue/5 dark:border-[#3d3d4d]">
                                <button
                                    onClick={onClose}
                                    className="w-full h-12 px-6 text-sm font-semibold text-white bg-siemens-petrol rounded hover:bg-siemens-petrol-dark transition-colors"
                                >
                                    Close
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </>
            )}
        </AnimatePresence>
    );
}

export default HistoryPanel;
